import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { PartnerInvoicesTable } from 'src/components/invoice/partner-invoices-table/PartnerInvoicesTable'
import { partnerInvoiceTypeLabels } from 'src/constants/invoice/constants'
import type {
  ComposedInvoice,
  PendingInvoiceAction,
  PendingPaymentReleaseAction,
} from 'src/interfaces/invoice'
import { formatCentsAsUsd } from 'src/utils/invoiceMoney'
import {
  deriveComposedInvoiceTypes,
  isComposedInvoiceGenerated,
  sumPartnerInvoiceAmounts,
} from 'src/utils/invoiceStatus'

interface Props {
  invoice: ComposedInvoice
  canManage: boolean
  onRunAction(action: PendingInvoiceAction): void
  onPaymentReleaseAction(action: PendingPaymentReleaseAction): void
}

interface Fact {
  label: string
  value: string
}

/**
 * The composed invoice's partner invoices as one table card: the invoice's facts are the
 * table's own header, the grid sits directly under them. Fills whatever height the page gives
 * it (`PartnerInvoicesTable` is `h-full`), so the grid scrolls inside the card.
 *
 * The facts repeat the list row's derived columns so the page stands on its own after a deep
 * link; every value comes from the same utils the list grid uses.
 */
const ComposedInvoiceDetailsCardComponent = ({
  invoice,
  canManage,
  onRunAction,
  onPaymentReleaseAction,
}: Props) => {
  const facts = useMemo<Fact[]>(() => {
    const types = deriveComposedInvoiceTypes(invoice)

    return [
      {
        label: 'Type',
        value:
          types.length === 0
            ? '—'
            : types
                .map((type) => {
                  return partnerInvoiceTypeLabels[type]
                })
                .join(', '),
      },
      { label: 'Amount', value: formatCentsAsUsd(sumPartnerInvoiceAmounts(invoice)) },
      { label: 'Late fee', value: formatCentsAsUsd(invoice.lateFeeAmount) },
      { label: 'Invoice generated', value: isComposedInvoiceGenerated(invoice) ? 'Yes' : 'No' },
      { label: 'Partner invoices', value: String(invoice.partnerInvoices.length) },
    ]
  }, [invoice])

  return (
    <div
      data-testid="composed-invoice-details-card"
      css={tw`flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-solid border-shades-100 bg-shades-0`}
    >
      <div
        css={tw`grid shrink-0 grid-cols-2 gap-4 border-0 border-b border-solid border-shades-100 p-4 md:grid-cols-5`}
      >
        {facts.map((fact) => {
          return (
            <div key={fact.label}>
              <Typography variant="h6" css={tw`text-shades-400`}>
                {fact.label}
              </Typography>
              <Typography variant="body2" css={tw`text-shades-1000`}>
                {fact.value}
              </Typography>
            </div>
          )
        })}
      </div>
      <div css={tw`min-h-0 flex-1`}>
        <PartnerInvoicesTable
          composedInvoice={invoice}
          canManage={canManage}
          onRunAction={onRunAction}
          onPaymentReleaseAction={onPaymentReleaseAction}
        />
      </div>
    </div>
  )
}

export const ComposedInvoiceDetailsCard = memo(ComposedInvoiceDetailsCardComponent)
